// Category (product type) selector for the header search form. Writes the chosen value
// into [data-search-scope-input] and notifies the surrounding <search-modal>, which
// folds it into the query as `product_type:X AND term`.
class SearchCollectionSelect extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.toggle = this.querySelector('[data-collection-toggle]');
    this.list = this.querySelector('[data-collection-list]');

    if (!this.toggle || !this.list) return;

    this.dataset.initialized = 'true';

    this.valueLabel = this.querySelector('[data-collection-value]');
    this.closeButton = this.querySelector('[data-collection-close]');
    this.scopeInput = this.closest('form')?.querySelector('[data-search-scope-input]');
    this.options = Array.from(this.list.querySelectorAll('[role="option"]'));

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.toggle.addEventListener('click', this.onToggleClick.bind(this), { signal });
    this.list.addEventListener('click', this.onListClick.bind(this), { signal });
    this.list.addEventListener('keydown', this.onListKeyDown.bind(this), { signal });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close(true), { signal });
    }

    document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
    document.addEventListener('keydown', this.onDocumentKeyDown.bind(this), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.dataset.initialized = 'false';
  }

  get isOpen() {
    return this.classList.contains('active');
  }

  onToggleClick(event) {
    event.preventDefault();

    if (this.isOpen) {
      this.close(true);
    } else {
      this.open();
    }
  }

  onListClick(event) {
    const option = event.target.closest('[role="option"]');

    if (option) this.select(option);
  }

  onListKeyDown(event) {
    const current = event.target.closest('[role="option"]');

    if (!current) return;

    const index = this.options.indexOf(current);
    let next = null;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = this.options[index + 1];
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = this.options[index - 1];
        break;
      case 'Home':
        next = this.options[0];
        break;
      case 'End':
        next = this.options[this.options.length - 1];
        break;
      case 'Tab':
        // Let focus leave the panel naturally instead of trapping it.
        this.close(false);
        return;
      default:
        return;
    }

    if (!next) return;

    event.preventDefault();
    this.setActiveOption(next);
    next.focus();
  }

  onDocumentClick(event) {
    if (this.isOpen && !this.contains(event.target)) this.close(false);
  }

  onDocumentKeyDown(event) {
    if (event.key === 'Escape' && this.isOpen) this.close(true);
  }

  select(option, notify = true) {
    const title = option.dataset.title || option.textContent.trim();

    if (this.valueLabel) this.valueLabel.textContent = title;
    if (this.scopeInput) this.scopeInput.value = option.dataset.value || '';

    this.options.forEach((item) => {
      const isSelected = item === option;

      item.classList.toggle('active', isSelected);
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    this.setActiveOption(option);
    this.close(true);

    if (notify) {
      this.dispatchEvent(new CustomEvent('search-scope:change', { bubbles: true }));
    }
  }

  // Called by <search-modal>.reset() so a reopened popup starts from "All categories".
  reset() {
    if (this.options?.length) this.select(this.options[0], false);
    this.close(false);
  }

  // Roving tabindex: only the current option is reachable with Tab.
  setActiveOption(option) {
    this.options.forEach((item) => {
      item.setAttribute('tabindex', item === option ? '0' : '-1');
    });
  }

  open() {
    this.classList.add('active');
    this.toggle.setAttribute('aria-expanded', 'true');

    const selected = this.options.find((item) => item.getAttribute('aria-selected') === 'true');
    const target = selected || this.options[0];

    this.setActiveOption(target);

    if (!target) return;

    // The panel is hidden with `visibility`, which only affects paint — a layout read
    // does not flush it, but a computed-style read does, and focus() is ignored until
    // the option is actually visible.
    void getComputedStyle(target).visibility;

    target.focus();
  }

  close(returnFocus) {
    if (!this.isOpen) return;

    this.classList.remove('active');
    this.toggle.setAttribute('aria-expanded', 'false');

    if (returnFocus) this.toggle.focus();
  }
}

// Opens and closes the predictive-search dropdown under the header search form. All the
// fetching and rendering belongs to <search-modal> (assets/search.js) — this only owns
// panel visibility, following the theme's drawer convention: `hidden` attribute plus an
// `.is-open` class for the transition.
class HeaderSearch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.modal = this.querySelector('search-modal');
    this.panel = this.querySelector('[data-header-search-panel]');
    this.input = this.querySelector('[data-search-input]');

    if (!this.modal || !this.panel || !this.input) return;

    this.dataset.initialized = 'true';
    this.closeTimer = null;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.input.addEventListener('focus', () => this.open(), { signal });
    this.input.addEventListener('click', () => this.open(), { signal });
    this.addEventListener('search-scope:change', () => this.open(), { signal });
    document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
    document.addEventListener('keydown', this.onDocumentKeyDown.bind(this), { signal });
  }

  disconnectedCallback() {
    window.clearTimeout(this.closeTimer);
    this.abortController?.abort();
    this.dataset.initialized = 'false';
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  onDocumentClick(event) {
    if (this.isOpen && !this.contains(event.target)) this.close();
  }

  onDocumentKeyDown(event) {
    // <search-modal> stops Escape from bubbling while the input still has a query, so
    // the first Escape clears the query and only the second one closes the panel.
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.input.focus();
    }
  }

  open() {
    window.clearTimeout(this.closeTimer);

    if (this.isOpen) return;

    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');

    window.requestAnimationFrame(() => {
      this.classList.add('is-open');
    });
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');

    // Let the exit transition finish before the panel leaves the layout. Unlike the
    // search drawer this does not reset <search-modal> — the form stays on the page, so
    // the query and category must survive closing (on the results page they are
    // pre-filled from the URL).
    this.closeTimer = window.setTimeout(() => {
      if (this.isOpen) return;

      this.panel.hidden = true;
    }, 300);
  }
}

if (!customElements.get('search-collection-select')) {
  customElements.define('search-collection-select', SearchCollectionSelect);
}

if (!customElements.get('header-search')) {
  customElements.define('header-search', HeaderSearch);
}
